import { useEffect, useRef } from "react";
import { getTurnstileSiteKey } from "../../config/turnstile";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;

function loadTurnstile() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.turnstile || null);
      s.onerror = () => {
        scriptPromise = null; // allow a retry on the next mount
        resolve(null);
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget. Renders nothing unless
 * REACT_APP_TURNSTILE_SITE_KEY is set. Calls onToken(token) when the challenge
 * passes and onToken("") when the token expires or the challenge errors.
 * Change `resetKey` to get a fresh challenge (a token works once).
 */
export default function TurnstileWidget({ onToken, resetKey = 0 }) {
  const siteKey = getTurnstileSiteKey();
  const holder = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !ts || !holder.current) return;
      widgetId.current = ts.render(holder.current, {
        sitekey: siteKey,
        theme: "dark",
        callback: (token) => onTokenRef.current && onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current && onTokenRef.current(""),
        "error-callback": () => onTokenRef.current && onTokenRef.current(""),
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch (_) {
          /* widget already gone */
        }
      }
      widgetId.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetKey === 0 || widgetId.current === null || !window.turnstile) return;
    try {
      window.turnstile.reset(widgetId.current);
    } catch (_) {
      /* ignore */
    }
  }, [resetKey]);

  if (!siteKey) return null;
  return <div ref={holder} className="integrate-modal__turnstile" data-testid="turnstile-widget" />;
}
